import React, { useEffect, useState } from "react";
import {
  Layout,
  Menu,
  Row,
  Typography,
  Button,
  ConfigProvider,
  Tooltip,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// ICON IMPORTS
import Users01 from "@untitled-ui/icons-react/build/esm/Users01";
import Menu01 from "@untitled-ui/icons-react/build/esm/Menu01";
import Home04 from "@untitled-ui/icons-react/build/esm/Home04";
import CurrencyDollarCircle from "@untitled-ui/icons-react/build/esm/CurrencyDollarCircle";
import CoinsStacked02 from "@untitled-ui/icons-react/build/esm/CoinsStacked02";
import Briefcase02 from "@untitled-ui/icons-react/build/esm/Briefcase02";
import Shield01 from "@untitled-ui/icons-react/build/esm/Shield01";
import UserCheck02 from "@untitled-ui/icons-react/build/esm/UserCheck02";
import LinkBroken02 from "@untitled-ui/icons-react/build/esm/LinkBroken02";
import BarChart10 from "@untitled-ui/icons-react/build/esm/BarChart10";
import CoinsHand from "@untitled-ui/icons-react/build/esm/CoinsHand";
import Tool02 from "@untitled-ui/icons-react/build/esm/Tool02";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import Package from "@untitled-ui/icons-react/build/esm/Package";
import Bank from "@untitled-ui/icons-react/build/esm/Bank";

// PROJECT IMPORTS
import MenuSearchBar from "../MenuSearchBar";
import logo from "../../assets/images/logo_stroke_gold.png";

import cnt from "../../services/definedConstant";
import { BOM } from "../../services/definedMethod";
import Wallet01 from "@untitled-ui/icons-react/build/esm/Wallet01";
import Wallet04 from "@untitled-ui/icons-react/build/esm/Wallet04";
import appConfig from "../../config/appConfig";

const { Sider } = Layout;
const { Text } = Typography;

export default function Sidebar(props) {
  const { collapsed, setCollapsed } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [selectedKey, setSelectedKey] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);

  function flattenKeys(menuList) {
    const keys = [];
    const traverse = (items) => {
      items.forEach((item) => {
        if (item.key) keys.push(item.key);
        if (item.children) traverse(item.children);
      });
    };
    traverse(menuList);
    return keys;
  }

  function getMatchedKey(pathname, keys) {
    let matched = null;

    keys.forEach((key) => {
      if (pathname.startsWith(key)) {
        if (!matched || key.length > matched.length) {
          matched = key;
        }
      }
    });

    if (!matched) {
      const pathSegments = pathname.split("/").filter(Boolean);

      for (let i = pathSegments.length - 1; i > 0; i--) {
        const parentPath = "/" + pathSegments.slice(0, i).join("/");

        keys.forEach((key) => {
          if (key === parentPath) {
            matched = key;
          }
        });

        if (matched) break;
      }
    }

    if (!matched) {
      const pathSegments = pathname.split("/").filter(Boolean);

      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];

        let entityName = null;
        if (lastSegment.startsWith("add-")) {
          entityName = lastSegment.substring(4);
        } else if (lastSegment.startsWith("edit-")) {
          entityName = lastSegment.substring(5);
        } else if (
          pathSegments.length >= 2 &&
          pathSegments[pathSegments.length - 2].startsWith("edit-")
        ) {
          entityName = pathSegments[pathSegments.length - 2].substring(5);
        }

        if (entityName) {
          const manageRoute = `/manage-${entityName}`;
          if (keys.includes(manageRoute)) {
            matched = manageRoute;
          } else {
            const pluralRoute = `/manage-${entityName}s`;
            if (keys.includes(pluralRoute)) {
              matched = pluralRoute;
            }
          }
        }
      }
    }

    return matched;
  }

  useEffect(() => {
    setSelectedKey([location.pathname]);
  }, [location.pathname]);

  const parentPermissions = {
    "/dashboard": "VIEW_DASHBOARD",
    customers: "VIEW_CUSTOMER",
    productConfig: "VIEW_PRODUCT",
    accountsContracts: "VIEW_ACCOUNTS_CONTRACTS",
    paymentsTransfers: "VIEW_PAYMENTS_TRANFERS",
    workflow: "VIEW_WORKFLOW",
    treasuryInvestments: "VIEW_TREASURY_INVESTMENT",
    reportsAnalytics: "VIEW_REPORTS",
    operations: "VIEW_OPERATIONS",
    glAndAccounting: "VIEW_ACCOUNTING",
    riskCompliance: "VIEW_RISK_COMPLIANCE",
    channelsIntegrations: "VIEW_CHANNELS_INTEGRATION",
    securityManagement: "VIEW_SECURITY",
    sysAdmin: "VIEW_SYSADMIN",
    sysAdminList: "VIEW_SYSADMIN",
    tellerOperations: "VIEW_TELLER_OPERATIONS",
    agencyBanking: "VIEW_AGENCY_BANKING",
    "/wallet-product": "WALLET_PRODUCT",
    "/wallet-account": "WALLET_ACCOUNT",

    walletProduct: "WALLET_PRODUCT",
    walletAccount: "WALLET_ACCOUNT",
  };

  const rawMenuList = [
    {
      label: "Dashboard",
      key: "/dashboard",
      icon: <Home04 style={{ width: "1em" }} />,
    },
    {
      label: "Customer",
      key: "customers",
      icon: <Users01 style={{ width: "1em" }} />,
      children: [
        {
          label: "Manage Customer",
          key: "/cst0100",
          disabled: !BOM.HasPermission("READ_CLIENT"),
        },
        {
          label: "Manage KYC",
          key: "/manage-kyc",
          disabled: !BOM.HasPermission("READ_KYC"),
        },
      ],
      // disabled: !BOM.HasPermission("VIEW_CUSTOMER"),
    },
    {
      label: "Product Config",
      key: "productConfig",
      icon: <Package style={{ width: "1em" }} />,
      children: [
        {
          label: "Savings Product",
          key: "/prd0100",
          disabled: !BOM.HasPermission("READ_SAVINGSPRODUCT"),
        },
        {
          label: "Loans & Overdraft",
          key: "/prd0200",
          disabled: !BOM.HasPermission("READ_LOANPRODUCT"),
        },
        {
          label: "Wallet Product",
          key: "/wallet-product",
          // icon: <Wallet04 style={{ width: "1em" }} />,
          // moduleKey: "walletProduct",
        },
      ],
      // disabled: !BOM.HasPermission("VIEW_PRODUCT"),
    },
    {
      label: "Accounts",
      key: "accountsContracts",
      icon: <CoinsHand style={{ width: "1em" }} />,
      children: [
        // {
        //   label: "Current",
        //   key: "/acc0101",
        //   disabled: !BOM.HasPermission("READ_CURRENTACCOUNTMENU"),
        // },
        {
          label: "Wallet Account",
          key: "/wallet-account",
          // icon: <Wallet01 style={{ width: "1em" }} />,
          // moduleKey: "walletAccount",
        },
        {
          label: "Savings",
          key: "/acc0102",
          disabled: !BOM.HasPermission("READ_SAVINGSACCOUNTMENU"),
        },
        {
          label: "Loans",
          key: "/acc0103",
          disabled: !BOM.HasPermission("READ_LOAN"),
        },
      ],
    },

    {
      label: "WorkFlow",
      key: "workflow",
      icon: <Briefcase02 style={{ width: "1em" }} />,
      children: [
        {
          label: "Loan Approval",
          key: "/loan-approval",
          disabled: !BOM.HasPermission("READ_LOAN"),
        },

        {
          label: "Loan Collections",
          key: "/loan-collections",
          disabled: !BOM.HasPermission("READ_LOAN_COLLECTION"),
        },
      ],
    },

    {
      label: (
        <Tooltip title="Reports & Analytics" placement="top">
          {BOM.SubstringFormat("Reports & Analytics", 15)}
        </Tooltip>
      ),
      key: "reportsAnalytics",
      icon: <BarChart10 style={{ width: "1em" }} />,
      children: [
        {
          label: "Customer Reports",
          key: "/customer-reports",
          disabled: !BOM.HasPermission("CUSTOMER_REPORTS"),
        },
        {
          label: "Loans Reports",
          key: "/loan-reports",
          disabled: !BOM.HasPermission("LOANS_REPORT"),
        },
        {
          label: "CASA Reports",
          key: "/casa-reports",
          disabled: !BOM.HasPermission("CASA_REPORTS"),
        },
        {
          label: "Wallet Reports",
          key: "/wallet-reports",
          disabled: !BOM.HasPermission("WALLET_REPORTS"),
        },
        {
          label: "Deposit Reports",
          key: "/deposit-reports",
          disabled: !BOM.HasPermission("DEPOSIT_REPORTS"),
        },
        {
          label: "Security Reports",
          key: "/security-reports",
          disabled: !BOM.HasPermission("SECURITY_REPORTS"),
        },
        {
          label: "Operations Reports",
          key: "/operations-reports",
          disabled: !BOM.HasPermission("OPERATIONS_REPORTS"),
        },

        {
          label: "Agency Banking Reports",
          key: "/agency-banking-reports",
          disabled: !BOM.HasPermission("AGENCY_BANKING_REPORTS"),
        },
        {
          label: "Custom Report Builder",
          key: "/custom-report-builder",
        },
      ],
      // disabled: !BOM.HasPermission("VIEW_REPORTS"),
    },
    {
      label: "Operations",
      key: "operations",
      icon: <Tool02 style={{ width: "1em" }} />,
      children: [
        {
          label: "Manage Task",
          key: "/manage-task",
          disabled: !BOM.HasPermission("READ_TASK"),
        },
        {
          label: "Reconciliation",
          key: "/reconciliation",
          disabled: !BOM.HasPermission("READ_RECONCILIATION"),
        },
        {
          label: "System Admin",
          key: "sysAdminList",
          children: [
            // Branch Management Submodule
            {
              label: "Branch Management",
              key: "branchManagement",
              children: [
                {
                  label: "Branch Setup",
                  key: "/sya0200",
                  // disabled: true,
                },
                {
                  label: "Branch Types",
                  key: "/sya0300",
                  // disabled: true,
                },
              ],
            },

            // Transaction Setup Submodule
            {
              label: "Transaction Setup",
              key: "transactionSetup",
              children: [
                {
                  label: "Account Numbering",
                  key: "/sya0100",
                  disabled: true,
                },
                {
                  label: "Till Configuration",
                  key: "/sya0600",
                },
                {
                  label: "Limit Rule Marker",
                  key: "/sya2600",
                  disabled: true,
                },
              ],
            },

            // Data Management Submodule
            {
              label: "Data Management",
              key: "dataManagement",
              children: [
                {
                  label: "Entity Table",
                  key: "/sya0900",
                  disabled: true,
                },
                {
                  label: "User Defined Table",
                  key: "/sya1000",
                  disabled: true,
                },
                {
                  label: "Entity Mapping",
                  key: "/sya1700",
                  disabled: true,
                },
                {
                  label: "Adhoc Query Setup",
                  key: "/sya2200",
                  disabled: true,
                },
                {
                  label: "Bulk Import",
                  key: "/sya1600",
                  disabled: true,
                },
              ],
            },

            // Client & Communication Submodule
            {
              label: "Client & Communication",
              key: "clientCommunication",
              children: [
                {
                  label: "SMS Campaign",
                  key: "/sya1400",
                  disabled: true,
                },
                {
                  label: "Customer Survey",
                  key: "/sya1800",
                  disabled: true,
                },
                {
                  label: "Client Category",
                  key: "/sya2500",
                  disabled: true,
                },
                {
                  label: "eChannel Setup",
                  key: "eChannelSetup",
                  disabled: true,
                },
              ],
            },

            // System Operations Submodule
            {
              label: "System Operations",
              key: "systemOperations",
              children: [
                {
                  label: "System Hook",
                  key: "/sya1500",
                  disabled: true,
                },
                {
                  label: "Batch Job Operations",
                  key: "/sya2300",
                  disabled: true,
                },
                {
                  label: "Bulk Loan Reassignment",
                  key: "/sya2400",
                  disabled: true,
                },
              ],
            },
          ],
          // disabled: !BOM.HasPermission("VIEW_SYSADMIN"),
        },
        {
          label: "Branch Configuration",
          key: "/sya0400",
        },
        {
          label: "Manage Vault/Teller",
          key: "/sya0500",
        },
      ],
      // disabled: !BOM.HasPermission("VIEW_OPERATIONS"),
    },
    {
      label: (
        <Tooltip title="General Ledger & Finance" placement="top">
          {BOM.SubstringFormat("General Ledger & Finance", 15)}
        </Tooltip>
      ),
      key: "glAndAccounting",
      icon: <CurrencyDollarCircle style={{ width: "1em" }} />,
      children: [
        {
          label: "Chart of Accounts",
          key: "/gla0100",
          disabled: !BOM.HasPermission("READ_GLACCOUNT"),
        },
        {
          label: "Journal Entries",
          key: "/gla0200",
          disabled: !BOM.HasPermission("READ_JOURNALENTRY"),
        },
        {
          label: "Manage Loan Provisioning",
          key: "/loan-provisioning",
          disabled: !BOM.HasPermission("READ_LOAN_PROVISIONING"),
        },
        {
          label: "Manage Revaluation",
          key: "/revaluation",
          disabled: !BOM.HasPermission("READ_REVALUATION"),
        },
        {
          label: "Finance Rules",
          key: "/gla0300",
          disabled: !BOM.HasPermission("READ_FINANCIAL_RULE"),
        },
        {
          label: "Accounting Rules",
          key: "/gla0500",
          disabled: !BOM.HasPermission("READ_ACCOUNTINGRULE"),
        },
        // {
        //   label: "FX Rate Type",
        //   key: "/prd0601",
        //   disabled: !BOM.HasPermission("READ_RATE_TYPE"),
        // },
        // {
        //   label: "FX Rate Setup",
        //   key: "/prd0602",
        //   disabled: !BOM.HasPermission("READ_FX_RATE"),
        // },
        {
          label: "Manage Accruals",
          key: "/gla0700",
          disabled: !BOM.HasPermission("EXECUTE_PERIODICACCRUALACCOUNTING"),
        },
        {
          label: "Manage End of Circle",
          key: "/gla0800",
          disabled: !BOM.HasPermission("READ_GLCLOSURE"),
        },
        {
          label: "GL Mapping",
          key: "/gla0400",
          disabled: !BOM.HasPermission("READ_FINANCIALACTIVITYACCOUNT"),
        },
        {
          label: "Frequent Posting",
          key: "/gla0600",
          disabled: !BOM.HasPermission("CREATE_JOURNALENTRY"),
        },
        {
          label: "Provisioning Entries",
          key: "/gla1100",
          disabled: !BOM.HasPermission("VIEW_PROVISIONING_ENTRIES"),
        },
        {
          label: "CASA to GL Transfer",
          key: "/gla1200",
          disabled: !BOM.HasPermission("CASA_TO_GL_TRANSFER"),
        },
        {
          label: "GL to CASA Transfer",
          key: "/gla1300",
          disabled: !BOM.HasPermission("GL_TO_CASA_TRANSFER"),
        },
        {
          label: "Floating Rates",
          key: "/prd0605",
          disabled: !BOM.HasPermission("READ_FLOATINGRATE"),
        },
        // {
        //   label: "Tax Management",
        //   key: "/prd1300",
        //   disabled: !BOM.HasPermission("READ_TAX_MANAGEMENT"),
        // },
        // {
        //   label: "Tax Group",
        //   key: "/prd1301",
        //   disabled: !BOM.HasPermission("READ_TAX_GROUP_GL"),
        // },
      ],
      // disabled: !BOM.HasPermission("VIEW_ACCOUNTING"),
    },
    {
      label: "Service Provider",
      key: "agencyBanking",
      icon: <Settings01 style={{ width: "1em" }} />,
      children: [
        {
          label: "Manage Service Provider",
          key: "/service-provider",
          disabled: !BOM.HasPermission("READ_AGENT_MANAGEMENT"),
        },
        // {
        //   label: "Agent Transactions",
        //   key: "/agent-transactions",
        //   disabled: !BOM.HasPermission("READ_AGENT_TRANSACTIONS"),
        // },
        {
          label: "Agent Reconciliation",
          key: "/agent-reconciliation",
          disabled: !BOM.HasPermission("READ_AGENT_RECONCILIATION"),
        },
        {
          label: "Agent Commission",
          key: "/agent-commission",
          disabled: !BOM.HasPermission("READ_AGENT_COMMISSION"),
        },
        // {
        //   label: "Agent Reports",
        //   key: "/agent-reports",
        //   disabled: !BOM.HasPermission("READ_AGENT_REPORTS"),
        // },
      ],
      // disabled: !BOM.HasPermission("VIEW_AGENCY_BANKING"),
    },
    {
      label: (
        <Tooltip title="Security Management" placement="top">
          {BOM.SubstringFormat("Security Management", 15)}
        </Tooltip>
      ),
      key: "securityManagement",
      icon: <UserCheck02 style={{ width: "1em" }} />,
      children: [
        {
          label: "Manage User",
          key: "/sms0100",
          disabled: !BOM.HasPermission("READ_USER"),
        },
        {
          label: "Manage Employees",
          key: "/sms0200",
          disabled: !BOM.HasPermission("READ_STAFF"),
        },
        {
          label: "Manage Roles & Permissions",
          key: "/sms0400",
          disabled: !BOM.HasPermission("READ_ROLE"),
        },
        {
          label: "Maker & Checker Setup",
          key: "/sms0600",
          disabled: !BOM.HasPermission("READ_PERMISSION"),
        },
        {
          label: "Audit Trail",
          key: "/sms0500",
          disabled: !BOM.HasPermission("READ_AUDIT"),
        },
        {
          label: "Manage API",
          key: "/manage-api",
          disabled: !BOM.HasPermission("READ_API"),
        },
        {
          label: "Checker Inbox",
          key: "/sms0800",
          disabled: !BOM.HasPermission("CHECKER_INBOX_TASKS"),
        },
      ],
      // disabled: !BOM.HasPermission("VIEW_SECURITY"),
    },
    {
      label: (
        <Tooltip title="System Administration" placement="top">
          {BOM.SubstringFormat("System Administration", 15)}
        </Tooltip>
      ),
      key: "sysAdmin",
      icon: <Settings01 style={{ width: "1em" }} />,
      children: [
        // {
        //   label: "Configuration",
        //   key: "/manage-config",
        //   disabled: !BOM.HasPermission("READ_CONFIGURATION"),
        // },
        {
          label: "Configurations",
          key: "sysConfig",
          children: [
            {
              label: "Core Parameters",
              key: "coreParameters",
              disabled: true,
            },
            {
              label: "Manage Funds",
              key: "manageFunds",
              disabled: true,
            },
            {
              label: "Manage Holiday",
              key: "/manage-holidays",
              disabled: !BOM.HasPermission("READ_HOLIDAY"),
            },
            {
              label: "Manage Workdays",
              key: "/manage-Workingdays",
              disabled: !BOM.HasPermission("READ_WORKINGDAYS"),
            },
            {
              label: "Manage Codes",
              key: "/manage-codes",
              disabled: !BOM.HasPermission("READ_CODE"),
            },
            {
              label: "Password Settings",
              key: "passwordSettings",
              disabled: true,
            },
            // {
            //   label: "Scheduler Jobs",
            //   key: "/manage-scheduler-jobs",
            //   disabled: !BOM.HasPermission("READ_SCHEDULER"),
            // },
            {
              label: "Global Settings Config",
              key: "/manage-config",
              disabled: !BOM.HasPermission("READ_CONFIGURATION"),
            },
            {
              label: "Account Settings",
              key: "accountSettings",
              disabled: true,
            },
            {
              label: "Manage Payment Type",
              key: "/manage-payment-type",
              disabled: !BOM.HasPermission("READ_PAYMENTTYPE"),
            },
            {
              label: "Manage Currency",
              key: "/manage-currency",
              disabled: !BOM.HasPermission("READ_CURRENCY"),
            },
            {
              label: "Manage Hooks",
              key: "manageHooks",
              disabled: true,
            },
            {
              label: "Entity Mapping",
              key: "entityMapping",
              disabled: true,
            },
            {
              label: "Manage KYC",
              key: "manageKyc",
              disabled: true,
            },
          ],
          // disabled: !BOM.HasPermission("VIEW_CONFIGURATIONS"),
        },
        // {
        //   label: "Workflow Designer",
        //   key: "/workflow-designer",
        //   disabled: !BOM.HasPermission("READ_WORKFLOW_DESIGNER"),
        // },

        {
          label: "Notification Setup",
          key: "/notification-setup",
          children: [
            {
              label: "Email Service Configuration",
              key: "/sya2700",
              disabled: !BOM.HasPermission("EMAIL_SERVICE"),
            },
            {
              label: "SMS Service Configuration",
              key: "/sya2800",
              disabled: !BOM.HasPermission("SMS_SERVICE"),
            },
          ],

          // disabled: !BOM.HasPermission("READ_NOTIFICATION_SETUP"),
        },

        // {
        //   label: "Manage AI Agent",
        //   key: "/manage-ai-agent",
        //   disabled: !BOM.HasPermission("READ_AI_AGENT"),
        // },
        {
          label: "Scheduler/Batch Jobs",
          key: "/manage-scheduler-jobs",
          disabled: !BOM.HasPermission("READ_SCHEDULER"),
        },
      ],
      // disabled: !BOM.HasPermission("VIEW_SYSADMIN"),
    },
    {
      label: "Teller Services",
      key: "tellerOperations",
      icon: <Settings01 style={{ width: "1em" }} />,
      children: [
        {
          label: "Link User to Till",
          key: "/tlo0100",
          disabled: !BOM.HasPermission("READ_TILL_LINK"),
        },
        {
          label: "Branch Batch",
          key: "/tlo0200",
          disabled: !BOM.HasPermission("READ_BRANCH_BATCH"),
        },
        {
          label: "Vault Batch",
          key: "/tlo0300",
          disabled: !BOM.HasPermission("READ_VAULT_BATCH"),
        },
        {
          label: "Teller Batch",
          key: "/tlo0400",
          disabled: !BOM.HasPermission("READ_TELLER_BATCH"),
        },
        {
          label: "Send Cash",
          key: "/tlo0500",
          disabled: !BOM.HasPermission("READ_SEND_CASH"),
        },
        {
          label: "Request Cash",
          key: "/tlo0600",
          disabled: !BOM.HasPermission("READ_REQUEST_CASH"),
        },
        {
          label: "Receive Teller Transactions",
          key: "/tlo0700",
          disabled: !BOM.HasPermission("READ_RECEIVE_TELLER"),
        },
        {
          label: "Cash Deposit",
          key: "/tlo0800",
          disabled: !BOM.HasPermission("READ_CASH_DEPOSIT"),
        },
        {
          label: "Cash Withdrawal",
          key: "/tlo0900",
          disabled: !BOM.HasPermission("READ_CASH_WITHDRAWAL"),
        },
        {
          label: "Cheque Deposit",
          key: "/tlo1000-not-liscensed",
          disabled: !BOM.HasPermission("READ_CHEQUE_DEPOSIT"),
        },
        {
          label: "Cheque Withdrawal",
          key: "/tlo1100-not-liscensed",
          disabled: !BOM.HasPermission("READ_CHEQUE_WITHDRAWAL"),
        },
        {
          label: "Fund Transfer",
          key: "/fundTransfer",
          disabled: !BOM.HasPermission("READ_FUND_TRANSFER"),
        },
      ],
      // disabled: !BOM.HasPermission("VIEW_TELLER"),
    },
  ];

  // ========================================================================
  // FILTER MENU: First by permissions, then by licensing
  // ========================================================================
  const menuList = rawMenuList.filter((item) => {
    if (item.key === "/dashboard") {
      return true;
    }

    // Check permission
    // const requiredPermission = parentPermissions[item.key];
    // if (requiredPermission && !BOM.HasPermission(requiredPermission)) {
    //   return false;
    // }
    const requiredPermission =
      parentPermissions[item.key] || parentPermissions[item.moduleKey];
    if (requiredPermission && !BOM.HasPermission(requiredPermission)) {
      return false;
    }

    // Check licensing (NEW)
    // if (!BOM.IsModuleLicensed(item.key)) {
    //   return false;
    // }
    const moduleToCheck = item.moduleKey || item.key;
    if (!BOM.IsModuleLicensed(moduleToCheck)) {
      return false;
    }

    return true;
  });

  const { pathname } = useLocation();
  const keys = flattenKeys(menuList);
  const matchedKey = getMatchedKey(pathname, keys);

  useEffect(() => {
    dispatch({
      type: cnt.CLEAR_RIGHT_BTN,
    });
  }, []);

  function onMenuSelect(menuConfig) {
    setSelectedKey([menuConfig.key]);
    navigate(menuConfig.key, { replace: false });
  }

  const sidebarTheme = {
    components: {
      Menu: {
        itemHoverBg: "#f6d0fe",
        itemSelectedBg: "#f6d0fe",
        itemHoverColor: "#9f1ab1",
        itemSelectedColor: "#9f1ab1",
      },
    },
  };

  return (
    <ConfigProvider theme={sidebarTheme}>
      <Sider
        trigger={null}
        collapsible
        collapsed={props.collapsed}
        style={{
          paddingTop: "1%",
          overflow: "auto",
          height: "100vh",
          width: collapsed ? "80px" : "280px",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
        }}
        theme="light"
      >
        <Row
          style={{
            display: "flex",
            flexDirection: collapsed && "column",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.3s ease",
          }}
        >
          <img
            src={logo}
            alt="logo"
            style={{
              height: props.logoDimensions,
              marginTop: collapsed && "20px",
              marginLeft: "20px",
              transition: "order 0.3s ease",
            }}
            className="sidebar-logo"
          />

          <Button
            type="text"
            icon={collapsed ? <Menu01 /> : <Menu01 />}
            onClick={() => setCollapsed(!collapsed)}
            shape="circle"
            size="large"
            style={{
              fontSize: "16px",
              marginLeft: "auto",
              marginRight: 20,
              order: collapsed && -1,
            }}
          />
        </Row>

        <div>
          {!collapsed && (
            <MenuSearchBar
              colorBgContainer={props.colorBgContainer}
              colorBorder="transparent"
              colorTextPlaceholder="#575757"
            />
          )}
        </div>

        <div
          hidden={props.menuSearchIconState}
          style={{
            width: 18,
            marginRight: "auto",
            marginLeft: "auto",
            marginTop: 18,
            marginBottom: 10,
          }}
        >
          <SearchOutlined style={{ color: "white", fontSize: "18px" }} />
        </div>

        <Menu
          theme={props.theme}
          mode="vertical"
          selectedKeys={matchedKey ? [matchedKey] : []}
          items={menuList}
          onClick={onMenuSelect}
        />

        <div
          style={{
            marginLeft: 10,
            marginTop: 20,
            padding: collapsed ? "8px" : "16px",
            textAlign: collapsed ? "center" : "left",
          }}
        >
          {!collapsed ? (
            <div>
              <Text
                style={{
                  fontSize: "12px",
                  color: "#666",
                  display: "block",
                }}
              >
                Version 2.2.0
              </Text>
              <a
                href="#"
                style={{
                  fontSize: "11px",
                  color: "#999",
                  textDecoration: "none",
                  opacity: 0.7,
                }}
                onMouseEnter={(e) => (e.target.style.opacity = 1)}
                onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
              >
                Activate
              </a>
            </div>
          ) : (
            <Tooltip title="Version 2.2.0" placement="right">
              <Text
                style={{
                  fontSize: "10px",
                  color: "#999",
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                }}
              >
                v2.2.0
              </Text>
            </Tooltip>
          )}
        </div>
      </Sider>
    </ConfigProvider>
  );
}
